import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectIsActiveCallScreenVisible,
  resetActiveCallScreen,
} from "../../../state/activeCallScreen/activeCallScreenSlice";
import {
  selectCallSessions,
  selectActiveCallId,
  setActiveCallId,
} from "../../../state/callSessions/callSessionsSlice";
import { showSnackbar } from "../../../state/snackbar/snackbarSlice";
import { initiateCallAction } from "../../../sagas/userAgent/initiateCall/action";
import { mergeCallSessionsAction } from "../../../sagas/conferenceCall/mergeCallSessions/action";
import { selectUserCredentials } from "../../../state/userCredential/userCredentialSlice";
import { selectCallAssistantQuota } from "../../../state/assistant/assistantQuotaSlice";
import { fetchCallAssistantQuotaAction } from "../../../sagas/assistant/fetchCallAssistantQuota/action";
import { getAutoStartCallAssistant } from "../../../browser/storage";

const withActiveCallManagement = (Component) => {
  return (props) => {
    const { drawerWidth, appBarHeight } = props;
    const [isMinimized, setIsMinimized] = useState(false);
    const [aiAssistantEvents, setAiAssistantEvents] = useState([]);
    const [leadContext, setLeadContext] = useState(null);
    const [position, setPosition] = useState({
      x: drawerWidth,
      y: appBarHeight,
    });
    const [isDialpadOpen, setIsDialpadOpen] = useState(false);
    const [isParkOverlayOpen, setIsParkOverlayOpen] = useState(false);
    const [dialpadMode, setDialpadMode] = useState(null);

    const [autoStartCallAssistant, setAutoStartCallAssistant] = useState(false);
    const [crmPanelOpen, setCrmExpanded] = useState(false);

    const dispatch = useDispatch();
    const callSessions = useSelector(selectCallSessions);
    const activeCallId = useSelector(selectActiveCallId);
    const activeCall = callSessions.find(
      (callSession) => callSession.id === activeCallId,
    );
    const isVisible = useSelector(selectIsActiveCallScreenVisible);
    const { callAssistantEnabled, defaultCrm } = useSelector(selectUserCredentials);
    const { quotaStatus: callQuotaStatus } = useSelector(
      selectCallAssistantQuota,
    );

    useEffect(() => {
      if (!isVisible) {
        closeDialpad();
        closeParkOverlay();
        setAiAssistantEvents([]);
        setLeadContext(null);
        setCrmExpanded(false);
      }
    }, [isVisible]);

    useEffect(() => {
      if (isVisible && callAssistantEnabled) {
        dispatch(fetchCallAssistantQuotaAction());
      }
    }, [isVisible, callAssistantEnabled, dispatch]);

    useEffect(() => {
      getAutoStartCallAssistant().then((val) => {
        setAutoStartCallAssistant(!!val);
      });
    }, []);

    //Auto-Start Call Assistant
    useEffect(() => {
      if (
        activeCall?.status === 'accepted' &&
        activeCall?.direction === 'outgoing' &&
        !activeCall.isRealtime &&
        !activeCall.isStartingRealtime &&
        callAssistantEnabled &&
        autoStartCallAssistant &&
        callQuotaStatus?.allowed
      ) {
        setAiAssistantEvents([]);
        activeCall.startRealtime(appendAiEvent);
      }
    }, [activeCall?.status, activeCall?.id, callAssistantEnabled, callQuotaStatus, autoStartCallAssistant]);

    // Adjust position dynamically when window is resized
    useEffect(() => {
      if (!isVisible) return;
      const handleResize = () => {
        const maxX = window.innerWidth - 615; // Updated maximum X based on window size
        const maxY = window.innerHeight - 750; // Updated maximum Y based on window size

        // Adjust position if it's out of bounds after resizing
        setPosition((prevPosition) => ({
          x: Math.max(Math.min(prevPosition.x, maxX), drawerWidth), // Ensuring position.x is between drawerWidth and maxX
          y: Math.max(Math.min(prevPosition.y, maxY), appBarHeight), // Ensuring position.y is between appBarHeight and maxY
        }));
      };

      window.addEventListener("resize", handleResize);
      handleResize();

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [isVisible, drawerWidth]);

    const handleCallEnd = () => {
      if (!activeCall) {
        console.log("No active call, clearing screen");
        dispatch(resetActiveCallScreen());
        return;
      }

      if (activeCall.status === "ended") {
        activeCall.cleanup();
        return;
      }

      if (activeCall.status === "accepted") {
        activeCall.bye();
      } else if (activeCall.status === "outgoingProgressing") {
        activeCall.cancel();
      }
    };

    const toggleMinimize = () => {
      setIsMinimized(!isMinimized);
    };

    const handleMuteToggle = () => {
      if (activeCall) {
        activeCall.isMuted ? activeCall.unmute() : activeCall.mute();
      }
    };

    const handleHoldToggle = () => {
      if (activeCall) {
        activeCall.isOnHold ? activeCall.unhold() : activeCall.hold();
      }
    };

    const handleRecordingToggle = () => {
      if (activeCall) {
        activeCall.isRecording
          ? activeCall.stopRecording()
          : activeCall.startRecording();
      }
    };

    const handleSendPrompt = useCallback((text) => {
      activeCall?.sendRealtimeMessage(text);
    }, [activeCall]);

    const appendAiEvent = useCallback((evt) => {
      if (evt.type === 'lead_context') {
        setLeadContext(evt.text);
        return;
      }
      setAiAssistantEvents((prev) => {
        if (evt.isReplace) {
          return prev.map((e) => e.id === evt.id ? { ...evt, isReplace: undefined } : e);
        }
        const next = [...prev, evt];
        return next.length > 200 ? next.slice(next.length - 200) : next;
      });
    }, []);

    const handleRealtime = () => {
      if (!activeCall) return;

      if (activeCall.isRealtime) {
        activeCall.stopRealtime();
        setAiAssistantEvents([]);
      } else {
        dispatch(fetchCallAssistantQuotaAction());
        setAiAssistantEvents([]);
        activeCall.startRealtime(appendAiEvent);
      }
    };

    const handleSwapCalls = () => {
      activeCall.hold();
      for (let i = 0; i < callSessions.length; i += 1) {
        if (activeCall.id !== callSessions[i].id) {
          callSessions[i].unhold();
          dispatch(setActiveCallId(callSessions[i].id));
          break;
        }
      }
    };

    const handleMergeCalls = () => {
      if (callSessions.length > 1) {
        // Find if there's an existing conference call
        const conferenceCall = callSessions.find(
          (session) => session.isConference,
        );
        if (conferenceCall) {
          if (conferenceCall.conferenceList.length <= 3) {
            // If there's a conference call and it has less than limit participants, transfer other calls to its bridge
            const conferenceBridgeNumber = conferenceCall.conferenceBridgeId;
            callSessions.forEach((session) => {
              if (!session.isConference) {
                session.transfer(conferenceBridgeNumber, true);
              }
            });
          } else {
            dispatch(
              showSnackbar({
                message: "Conference call participant limit reached.",
                severity: "warning",
              }),
            );
          }
        } else {
          // If no conference call exists, dispatch a action to get conference bridge
          let callIds = callSessions
            .map((session) => {
              let callId;
              if (session.direction === "outgoing") {
                callId =
                  session.session.outgoingRequestMessage.callId + "xfer_term,";
              } else if (session.direction === "incoming") {
                callId =
                  session.session.incomingInviteRequest.message.callId +
                  "xfer_orig,";
              }
              return callId;
            })
            .join("");

          dispatch(mergeCallSessionsAction(callIds)); // Dispatch action to merge calls
        }
      }
    };

    const handleAssistedTransfer = (phoneNumber) => {
      activeCall.initiateAssistedTransfer();
      dispatch(
        initiateCallAction({
          phoneNumber,
          isTransferTarget: true,
          sourceCallId: activeCall.id,
        }),
      );
    };

    const handleCompleteTransfer = () => {
      if (
        activeCall?.transferState.isTransferTarget &&
        activeCall.transferState.linkedSession
      ) {
        activeCall.transferState.linkedSession.completeAssistedTransfer();
      }
    };

    const handleCancelTransfer = () => {
      if (
        activeCall?.transferState.isTransferTarget &&
        activeCall.transferState.linkedSession
      ) {
        activeCall.transferState.linkedSession.cancelAssistedTransfer();
      }
    };

    const handleDrag = useCallback((e, data) => {
      setPosition({ x: data.x, y: data.y });
    }, []);

    const openDialpad = (mode) => {
      setDialpadMode(mode);
      setIsDialpadOpen(true);
    };

    const closeDialpad = () => {
      setIsDialpadOpen(false);
      setDialpadMode(null);
    };

    const openParkOverlay = () => {
      setIsParkOverlayOpen(true);
    };

    const closeParkOverlay = () => {
      setIsParkOverlayOpen(false);
    };

    return (
      <Component
        {...props}
        activeCall={activeCall}
        callSessions={callSessions}
        isVisible={isVisible}
        isMinimized={isMinimized}
        onCallEnd={handleCallEnd}
        toggleMinimize={toggleMinimize}
        onMuteToggle={handleMuteToggle}
        onHoldToggle={handleHoldToggle}
        onRecordingToggle={handleRecordingToggle}
        callAssistantEnabled={callAssistantEnabled}
        callQuotaStatus={callQuotaStatus}
        crmType={defaultCrm}
        onRealtimeToggle={handleRealtime}
        onSendPrompt={handleSendPrompt}
        aiAssistantEvents={aiAssistantEvents}
        crmPanelOpen={crmPanelOpen}
        onCrmPanelToggle={() => setCrmExpanded(v => !v)}
        leadContext={leadContext}
        onSwapCalls={handleSwapCalls}
        onMergeCalls={handleMergeCalls}
        position={position}
        onDrag={handleDrag}
        openDialpad={openDialpad}
        isDialpadOpen={isDialpadOpen}
        closeDialpad={closeDialpad}
        dialpadMode={dialpadMode}
        onAssistedTransfer={handleAssistedTransfer}
        onCompleteTransfer={handleCompleteTransfer}
        onCancelTransfer={handleCancelTransfer}
        openParkOverlay={openParkOverlay}
        isParkOverlayOpen={isParkOverlayOpen}
        closeParkOverlay={closeParkOverlay}
      />
    );
  };
};

export default withActiveCallManagement;
